import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';

export const fetchCompetitionSubmissions = createAsyncThunk(
  'gallery/fetchSubmissions',
  async (
    {
      competitionId,
      sortBy,
      page,
      pageSize,
      isPublic,
    }: {
      competitionId: string;
      sortBy?: string;
      page?: number;
      pageSize?: number;
      isPublic?: boolean;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const api = await apiClient.getCompetitionApi();
      const response = isPublic
        ? await api.competitionApiGetPublicCompetitionSubmissions(
            { competitionId, page, pageSize, sortBy },
            { signal },
          )
        : await api.competitionApiGetCompetitionSubmissions(
            {
              competitionId,
              page,
              pageSize,
              sortBy,
            },
            { signal },
          );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchMySubmissions = createAsyncThunk(
  'gallery/fetchMySubmissions',
  async (
    { competitionId }: { competitionId: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetMySubmissions({ competitionId }, { signal });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchSingleSubmission = createAsyncThunk(
  'gallery/fetchSingleSubmission',
  async (
    {
      competitionId,
      entryId,
      isPublic,
    }: { competitionId: string; entryId: string; isPublic?: boolean },
    { rejectWithValue, signal },
  ) => {
    try {
      const api = await apiClient.getCompetitionApi();
      // Anonymous viewers (share links) hit the public read-only route; logged-in
      // viewers use the authed route so is_liked is populated.
      const response = isPublic
        ? await api.competitionApiGetPublicSingleSubmission(
            { competitionId, entryId },
            { signal },
          )
        : await api.competitionApiGetSingleSubmission(
            { competitionId, entryId },
            {
              signal,
            },
          );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const toggleSubmissionLike = createAsyncThunk(
  'gallery/toggleLike',
  async (
    {
      competitionId,
      registrationId,
    }: { competitionId: string; registrationId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiToggleSubmissionLike({
        competitionId,
        entryId: registrationId,
      });
      return { registrationId, ...response.data };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
